import {
  mailAccountList,
  mailAccountById,
  mailAccountCreate,
  mailAccountUpdate,
  mailAccountDestroy,
  mailAccountTest,
  mailAccountSync,
  mailInboxList,
  mailInboxMessages,
  mailInboxStats,
  mailThreadById,
  mailThreadMessages,
  mailThreadUpdate,
  mailThreadArchive,
  mailThreadUnarchive,
  mailThreadMute,
  mailThreadUnmute,
  mailThreadDelete,
  mailMessageDestroy,
  mailMessageAnalysis,
  mailMessageReanalyze,
  mailMessageAnalyzeNow,
  mailSuggestionList,
  mailSuggestionUpdate,
  mailCreateReply,
  mailOutboundUpdate,
  mailOutboundSend,
  mailOutboundDelete,
  mailLeadList,
  mailLeadById,
  mailLeadUpdate,
  mailLeadInteractions,
  mailLeadEnrich,
  mailLeadAddNote,
  mailLeadFunnelStats,
  mailLeadScheduleMeeting,
  mailLeadGenerateProposal,
  mailLeadProposalPdf,
  mailLeadCompanyContext,
} from '@/services/mailHub.service'
import { mailLeadConvertToCampaign } from '@/services/agency/campaignOrders.service'

export default {
  namespaced: true,

  state: {
    accountList: { data: [], meta: { pagination: { total: 0 } } },
    accountListStatus: 'init',
    currentAccount: null,

    inboxList: { data: [], meta: { pagination: { total: 0 } } },
    inboxListStatus: 'init',
    inboxStats: null,

    currentThread: null,
    currentThreadStatus: 'init',
    threadMessages: { data: [], meta: { pagination: { total: 0 } } },
    threadMessagesStatus: 'init',

    connectionTestResult: null,

    // Phase 2
    currentAnalysis: null,
    currentAnalysisStatus: 'init', // init, pending, loaded
    suggestionList: { data: [], meta: { pagination: { total: 0 } } },
    suggestionListStatus: 'init',

    // Phase 4
    leadList: { data: [], meta: { pagination: { total: 0 } } },
    leadListStatus: 'init',
    currentLead: null,
    currentLeadStatus: 'init',
    leadInteractions: { data: [], meta: { pagination: { total: 0 } } },
    funnelStats: null,
  },

  getters: {
    getAccountList: state => state.accountList,
    getAccountListStatus: state => state.accountListStatus,
    getCurrentAccount: state => state.currentAccount,

    getInboxList: state => state.inboxList,
    getInboxListStatus: state => state.inboxListStatus,
    getInboxStats: state => state.inboxStats,

    getCurrentThread: state => state.currentThread,
    getCurrentThreadStatus: state => state.currentThreadStatus,
    getThreadMessages: state => state.threadMessages,
    getThreadMessagesStatus: state => state.threadMessagesStatus,

    getConnectionTestResult: state => state.connectionTestResult,

    // Phase 2
    getCurrentAnalysis: state => state.currentAnalysis,
    getCurrentAnalysisStatus: state => state.currentAnalysisStatus,
    getSuggestionList: state => state.suggestionList,
    getSuggestionListStatus: state => state.suggestionListStatus,

    // Phase 4
    getLeadList: state => state.leadList,
    getLeadListStatus: state => state.leadListStatus,
    getCurrentLead: state => state.currentLead,
    getCurrentLeadStatus: state => state.currentLeadStatus,
    getLeadInteractions: state => state.leadInteractions,
    getFunnelStats: state => state.funnelStats,
  },

  mutations: {
    SET_ACCOUNT_LIST(state, payload) { state.accountList = payload },
    SET_ACCOUNT_LIST_STATUS(state, status) { state.accountListStatus = status },
    SET_CURRENT_ACCOUNT(state, payload) { state.currentAccount = payload },

    SET_INBOX_LIST(state, payload) { state.inboxList = payload },
    SET_INBOX_LIST_STATUS(state, status) { state.inboxListStatus = status },
    SET_INBOX_STATS(state, payload) { state.inboxStats = payload },

    SET_CURRENT_THREAD(state, payload) { state.currentThread = payload },
    SET_CURRENT_THREAD_STATUS(state, status) { state.currentThreadStatus = status },
    SET_THREAD_MESSAGES(state, payload) { state.threadMessages = payload },
    SET_THREAD_MESSAGES_STATUS(state, status) { state.threadMessagesStatus = status },

    SET_CONNECTION_TEST_RESULT(state, payload) { state.connectionTestResult = payload },

    // Phase 2
    SET_CURRENT_ANALYSIS(state, payload) { state.currentAnalysis = payload },
    SET_CURRENT_ANALYSIS_STATUS(state, status) { state.currentAnalysisStatus = status },
    SET_SUGGESTION_LIST(state, payload) { state.suggestionList = payload },
    SET_SUGGESTION_LIST_STATUS(state, status) { state.suggestionListStatus = status },

    // Phase 4
    SET_LEAD_LIST(state, payload) { state.leadList = payload },
    SET_LEAD_LIST_STATUS(state, status) { state.leadListStatus = status },
    SET_CURRENT_LEAD(state, payload) { state.currentLead = payload },
    SET_CURRENT_LEAD_STATUS(state, status) { state.currentLeadStatus = status },
    SET_LEAD_INTERACTIONS(state, payload) { state.leadInteractions = payload },
    SET_FUNNEL_STATS(state, payload) { state.funnelStats = payload },
  },

  actions: {
    // ── Accounts ──────────────────────────────────────
    async fetchAccountList({ commit }, params) {
      commit('SET_ACCOUNT_LIST_STATUS', 'pending')
      try {
        const { data } = await mailAccountList(params)
        commit('SET_ACCOUNT_LIST', data)
        commit('SET_ACCOUNT_LIST_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_ACCOUNT_LIST_STATUS', 'error')
        throw error
      }
    },

    async fetchAccountById({ commit }, id) {
      try {
        const { data } = await mailAccountById(id)
        commit('SET_CURRENT_ACCOUNT', data.data)
        return data
      } catch (error) {
        throw error
      }
    },

    async createAccount({ dispatch }, payload) {
      const { data } = await mailAccountCreate(payload)
      return data
    },

    async updateAccount({ dispatch }, { id, payload }) {
      const { data } = await mailAccountUpdate(id, payload)
      return data
    },

    async deleteAccount({ dispatch }, id) {
      await mailAccountDestroy(id)
    },

    async testAccountConnection({ commit }, id) {
      commit('SET_CONNECTION_TEST_RESULT', null)
      try {
        const { data } = await mailAccountTest(id)
        commit('SET_CONNECTION_TEST_RESULT', data)
        return data
      } catch (error) {
        commit('SET_CONNECTION_TEST_RESULT', { data: { status: 'error', error: error.message } })
        throw error
      }
    },

    async syncAccount(_, id) {
      const { data } = await mailAccountSync(id)
      return data
    },

    // ── Inbox ─────────────────────────────────────────
    async fetchInboxList({ commit }, params) {
      commit('SET_INBOX_LIST_STATUS', 'pending')
      try {
        const { data } = await mailInboxList(params)
        commit('SET_INBOX_LIST', data)
        commit('SET_INBOX_LIST_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_INBOX_LIST_STATUS', 'error')
        throw error
      }
    },

    async fetchInboxMessages({ commit }, params) {
      commit('SET_INBOX_LIST_STATUS', 'pending')
      try {
        const { data } = await mailInboxMessages(params)
        commit('SET_INBOX_LIST', data)
        commit('SET_INBOX_LIST_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_INBOX_LIST_STATUS', 'error')
        throw error
      }
    },

    async fetchInboxStats({ commit }, params) {
      try {
        const { data } = await mailInboxStats(params)
        let payload = data?.data || data
        if (payload && payload.status_code !== undefined) {
          payload = payload.data
        }
        commit('SET_INBOX_STATS', payload)
        return payload
      } catch (error) {
        return null
      }
    },

    // ── Threads ───────────────────────────────────────
    async fetchThread({ commit }, id) {
      commit('SET_CURRENT_THREAD_STATUS', 'pending')
      try {
        const { data } = await mailThreadById(id)
        commit('SET_CURRENT_THREAD', data.data)
        commit('SET_CURRENT_THREAD_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_CURRENT_THREAD_STATUS', 'error')
        throw error
      }
    },

    async fetchThreadMessages({ commit }, { id, params }) {
      commit('SET_THREAD_MESSAGES_STATUS', 'pending')
      try {
        const { data } = await mailThreadMessages(id, params)
        commit('SET_THREAD_MESSAGES', data)
        commit('SET_THREAD_MESSAGES_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_THREAD_MESSAGES_STATUS', 'error')
        throw error
      }
    },

    async updateThread(_, { id, payload }) {
      const { data } = await mailThreadUpdate(id, payload)
      return data
    },

    async archiveThread(_, id) {
      const { data } = await mailThreadArchive(id)
      return data
    },

    async unarchiveThread(_, id) {
      const { data } = await mailThreadUnarchive(id)
      return data
    },

    async muteThread(_, id) {
      const { data } = await mailThreadMute(id)
      return data
    },

    async unmuteThread(_, id) {
      const { data } = await mailThreadUnmute(id)
      return data
    },

    async deleteThread(_, id) {
      await mailThreadDelete(id)
    },

    async deleteMessage(_, messageUuid) {
      await mailMessageDestroy(messageUuid)
    },

    // ── Analysis (Phase 2) ────────────────────────────
    async fetchAnalysis({ commit }, messageUuid) {
      commit('SET_CURRENT_ANALYSIS_STATUS', 'pending')
      try {
        const response = await mailMessageAnalysis(messageUuid)

        // Unwrap Dingo double-wrapping: response.data = {data: {message, data, status_code}} or {data: {id, category, ...}}
        let payload = response.data?.data || response.data

        // If it's a sendResponse wrapper (has message + status_code), unwrap one more level
        if (payload && payload.status_code !== undefined) {
          payload = payload.data
        }

        // Check if it's a status placeholder (not a real analysis)
        if (!payload || !payload.id) {
          commit('SET_CURRENT_ANALYSIS', null)
          commit('SET_CURRENT_ANALYSIS_STATUS', 'init')
          return null
        }

        commit('SET_CURRENT_ANALYSIS', payload)
        commit('SET_CURRENT_ANALYSIS_STATUS', 'loaded')
        return payload
      } catch (error) {
        commit('SET_CURRENT_ANALYSIS', null)
        commit('SET_CURRENT_ANALYSIS_STATUS', 'init')
        return null
      }
    },

    async reanalyzeMessage(_, { messageUuid, profile, provider, force }) {
      const { data } = await mailMessageReanalyze(messageUuid, { profile, provider, force })
      return data
    },

    async analyzeNow({ commit }, { messageUuid, profile, provider, force }) {
      commit('SET_CURRENT_ANALYSIS_STATUS', 'pending')
      try {
        const response = await mailMessageAnalyzeNow(messageUuid, { profile, provider, force })
        let payload = response.data?.data || response.data
        if (payload && payload.status_code !== undefined) {
          payload = payload.data
        }
        if (payload && payload.id) {
          commit('SET_CURRENT_ANALYSIS', payload)
          commit('SET_CURRENT_ANALYSIS_STATUS', 'loaded')
          return payload
        }
        commit('SET_CURRENT_ANALYSIS', null)
        commit('SET_CURRENT_ANALYSIS_STATUS', 'init')
        return null
      } catch (error) {
        commit('SET_CURRENT_ANALYSIS', null)
        commit('SET_CURRENT_ANALYSIS_STATUS', 'init')
        throw error
      }
    },

    // ── Suggestions (Phase 2) ─────────────────────────
    async fetchSuggestionList({ commit }, params) {
      commit('SET_SUGGESTION_LIST_STATUS', 'pending')
      try {
        const { data } = await mailSuggestionList(params)
        commit('SET_SUGGESTION_LIST', data)
        commit('SET_SUGGESTION_LIST_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_SUGGESTION_LIST_STATUS', 'error')
        throw error
      }
    },

    async updateSuggestion(_, { id, payload }) {
      const { data } = await mailSuggestionUpdate(id, payload)
      return data
    },

    // ── Outbound / Reply (Phase 3) ────────────────────
    async createReply(_, { messageUuid, payload }) {
      const { data } = await mailCreateReply(messageUuid, payload)
      return data
    },

    async updateOutbound(_, { id, payload }) {
      const { data } = await mailOutboundUpdate(id, payload)
      return data
    },

    async sendOutbound(_, id) {
      const { data } = await mailOutboundSend(id)
      return data
    },

    async deleteOutbound(_, id) {
      await mailOutboundDelete(id)
    },

    // ── Leads (Phase 4) ───────────────────────────────
    async fetchLeadList({ commit }, params) {
      commit('SET_LEAD_LIST_STATUS', 'pending')
      try {
        const { data } = await mailLeadList(params)
        commit('SET_LEAD_LIST', data)
        commit('SET_LEAD_LIST_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_LEAD_LIST_STATUS', 'error')
        throw error
      }
    },

    async fetchLead({ commit }, id) {
      commit('SET_CURRENT_LEAD_STATUS', 'pending')
      try {
        const { data } = await mailLeadById(id)
        commit('SET_CURRENT_LEAD', data.data)
        commit('SET_CURRENT_LEAD_STATUS', 'init')
        return data
      } catch (error) {
        commit('SET_CURRENT_LEAD_STATUS', 'error')
        throw error
      }
    },

    async updateLead(_, { id, payload }) {
      const { data } = await mailLeadUpdate(id, payload)
      return data
    },

    async fetchLeadInteractions({ commit }, { id, params }) {
      const { data } = await mailLeadInteractions(id, params)
      commit('SET_LEAD_INTERACTIONS', data)
      return data
    },

    async enrichLead(_, id) {
      const { data } = await mailLeadEnrich(id)
      return data
    },

    async addLeadNote(_, { id, summary }) {
      const { data } = await mailLeadAddNote(id, { summary })
      return data
    },

    async fetchFunnelStats({ commit }, params) {
      const { data } = await mailLeadFunnelStats(params)
      commit('SET_FUNNEL_STATS', data.data)
      return data
    },

    async scheduleLeadMeeting(_, { id, payload }) {
      const { data } = await mailLeadScheduleMeeting(id, payload)
      return data
    },

    async generateLeadProposal(_, { id, payload }) {
      const { data } = await mailLeadGenerateProposal(id, payload)
      return data
    },

    async downloadLeadProposalPdf(_, { id, documentId }) {
      const response = await mailLeadProposalPdf(id, documentId)
      return response.data
    },

    async convertLeadToCampaign(_, { id }) {
      const { data } = await mailLeadConvertToCampaign(id)
      return data
    },

    async fetchLeadCompanyContext(_, { id }) {
      const { data } = await mailLeadCompanyContext(id)
      return data
    },
  },
}
